import React, { useState, useMemo } from 'react';
import { useContent } from '../hooks/useContent';
import styles from './Investors.module.css';

const MRP_BY_YEAR = {
  2025: 3932,
  2026: 4325,
  2027: 4670,
  2028: 5000,
  2029: 5350,
  2030: 5730,
};

function Calculator({ inv }) {
  const calc = inv.calc;
  const [scenarioId, setScenarioId] = useState(calc.scenarios[0].id);
  const [places, setPlaces] = useState(100);

  const scenario = calc.scenarios.find(s => s.id === scenarioId);

  const rows = useMemo(() => {
    return Object.entries(MRP_BY_YEAR).map(([year, mrp]) => {
      const amount = scenario.mrpRate * mrp * places;
      return { year: Number(year), mrp, amount };
    });
  }, [scenarioId, places, scenario]);

  const total = rows.reduce((s, r) => s + r.amount, 0);
  const fmt = (n) => n.toLocaleString('ru-RU') + ' ₸';

  return (
    <div className={styles.calc}>
      <div className={styles.calcHeader}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/>
          <line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/>
        </svg>
        {calc.title}
      </div>

      <div className={styles.calcControls}>
        <div className={styles.calcField}>
          <label className={styles.calcLabel}>{calc.territory}</label>
          <div className={styles.calcScenarios}>
            {calc.scenarios.map(s => (
              <button
                key={s.id}
                className={`${styles.calcScenarioBtn} ${scenarioId === s.id ? styles.calcScenarioBtnActive : ''}`}
                onClick={() => setScenarioId(s.id)}
              >
                <span className={styles.calcScenarioMrp}>{s.mrpRate} {calc.mrpSuffix}</span>
                <span className={styles.calcScenarioName}>{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.calcField}>
          <label className={styles.calcLabel}>{calc.volumeLabel}</label>
          <div className={styles.calcInputWrap}>
            <input
              type="number"
              max={10000}
              value={places}
              onChange={e => {
                const val = e.target.value;
                setPlaces(val === '' ? '' : Math.max(1, Number(val)));
              }}
              className={styles.calcInput}
            />
            <span className={styles.calcInputUnit}>{calc.places}</span>
          </div>
        </div>
      </div>

      <div className={styles.calcResultWrap}>
        <div className={styles.calcResultTitle}>
          {calc.resultTitle} {scenario.mrpRate} {calc.mrpSuffix} × {calc.yearMrp} × {places} {calc.places}
        </div>
        <table className={styles.calcTable}>
          <thead>
            <tr>
              {calc.tableHead.map((h) => <th key={h}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.year}>
                <td>{r.year}</td>
                <td>{r.mrp.toLocaleString('ru-RU')} ₸</td>
                <td>{scenario.mrpRate} {calc.mrpSuffix}</td>
                <td className={styles.calcAmountCell}>{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={styles.calcTotalRow}>
              <td colSpan={3}>{calc.total}</td>
              <td className={styles.calcTotalAmount}>{fmt(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function Investors() {
  const { ui } = useContent();
  const inv = ui.investors;
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className={styles.section} id="investors">
      <div className={styles.bgGlow} />

      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.badge}>{inv.label}</span>
          <h2 className={styles.title}>{inv.mainTitle}</h2>
        </div>

        <div className={styles.stats}>
          {inv.stats.map((s, i) => (
            <div className={styles.statCard} key={i}>
              <div className={styles.statValue}>{s.value}</div>
              <div className={styles.statLabel}>{s.label}</div>
              <div className={styles.statSub}>{s.sub}</div>
            </div>
          ))}
        </div>

        <div className={styles.tabs}>
          {inv.tabs.map((t, i) => (
            <button
              key={i}
              className={`${styles.tab} ${activeTab === i ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className={styles.content}>
          {activeTab === 0 && (
            <div className={styles.disclosures}>
              <div className={styles.disclosure}>
                <div className={styles.disclosureLeft}>
                  <div className={styles.disclosureDate}>{inv.tab0.conditionLabel}</div>
                  <div className={styles.disclosureCategory}>{inv.tab0.category}</div>
                </div>
                <div className={styles.disclosureTitle}>{inv.tab0.disclosure}</div>
              </div>

              <div className={styles.condCards}>
                <div className={styles.condCard}>
                  <div className={styles.condCardIcon}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div className={styles.condCardTag}>{inv.tab0.cards[0].tag}</div>
                  <div className={styles.condCardTitle}>{inv.tab0.cards[0].title}</div>
                  <div className={styles.condCardText}>{inv.tab0.cards[0].text}</div>
                </div>

                <div className={styles.condCard}>
                  <div className={styles.condCardIcon} style={{ background: 'rgba(0, 56, 198, 0.12)', borderColor: 'rgba(59, 94, 231, 0.3)', color: '#003bc6' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>
                  <div className={styles.condCardTag} style={{ color: '#4da8da' }}>{inv.tab0.cards[1].tag}</div>
                  <div className={styles.condCardTitle}>{inv.tab0.cards[1].title}</div>
                  <div className={styles.condCardText}>
                    <span className={styles.condCardHighlight}>{inv.tab0.cards[1].text1}</span> {inv.tab0.cards[1].text1strong}
                  </div>
                  <div className={styles.condCardText} style={{ marginTop: 10 }}>
                    <span className={styles.condCardHighlight}>{inv.tab0.cards[1].text2}</span> {inv.tab0.cards[1].text2strong}
                  </div>
                </div>

                <div className={styles.condCard}>
                  <div className={styles.condCardIcon} style={{ background: 'rgba(0, 60, 255, 0.1)', borderColor: 'rgba(0, 60, 255, 0.25)', color: '#0060f0' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </div>
                  <div className={styles.condCardTag} style={{ color: '#4da8da' }}>{inv.tab0.cards[2].tag}</div>
                  <div className={styles.condCardTitle}>{inv.tab0.cards[2].title}</div>
                  <div className={styles.condCardText}>{inv.tab0.cards[2].text}</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className={styles.financials}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {inv.tab1.tableHead.map((h) => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {inv.tab1.rows.map((row) => (
                    <tr key={row[0]}>
                      <td>{row[0]}</td>
                      <td>{row[1]}</td>
                      <td>{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 2 && (
            <div className={styles.governance}>
              <div className={styles.govGrid}>
                {inv.tab2.steps.map((step, i) => (
                  <div className={styles.govCard} key={i}>
                    <div className={styles.govAvatar}>{i + 1}</div>
                    <div className={styles.govName}>{inv.tab2.stepLabel} {i + 1}</div>
                    <div className={styles.govRole}>{step}</div>
                  </div>
                ))}
              </div>
              <div className={styles.govNote}>{inv.tab2.note}</div>
            </div>
          )}

          {activeTab === 3 && (
            <div className={styles.bonds}>
              {inv.tab3.support.map((item) => (
                <div className={styles.bondCard} key={item.title}>
                  <div className={styles.bondHeader}>
                    <span className={styles.bondName}>{item.title}</span>
                    <span className={styles.bondIsin}>{inv.tab3.supportSection}</span>
                  </div>
                  <div className={styles.bondStats}>
                    <div>
                      <span>{inv.tab3.description}</span>
                      <strong>{item.desc}</strong>
                    </div>
                  </div>
                </div>
              ))}
              <div className={styles.calcInSupport}>
                <Calculator inv={inv} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
